import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, Legend, LabelList
} from 'recharts';
import {
  Users, TrendingUp, IndianRupee, Star, Target, FileText, ExternalLink,
  ChevronRight, RefreshCw, Calendar, Award, Zap, ArrowLeft, Settings,
  ChevronDown, ChevronUp, User, AlertCircle, Download
} from 'lucide-react';
import { toast } from 'sonner';

// ─── DESIGN TOKENS ─────────────────────────────────────────
const FONT = { heading: "'Manrope', sans-serif", body: "'IBM Plex Sans', sans-serif", data: "'JetBrains Mono', monospace" };
const COLOR = { navy: '#0f172a', accent: '#2563eb', success: '#10b981', warning: '#f59e0b', error: '#ef4444', border: '#e2e8f0', surface: '#f8fafc', muted: '#64748b' };

const getStatusColor = (pct) => pct >= 100 ? COLOR.success : pct >= 60 ? COLOR.warning : COLOR.error;
const getStatusBg = (pct) => pct >= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : pct >= 60 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200';

// ─── UTILITIES ─────────────────────────────────────────────
const formatCurrency = (val) => {
  if (!val) return '0';
  return Number(val).toLocaleString('en-IN');
};
const parseNum = (v) => { if (v === null || v === undefined) return 0; const num = parseFloat(String(v).replace(/,/g, '')); return isNaN(num) ? 0 : num; };

// ─── RATING STARS ──────────────────────────────────────────
function RatingStars({ rating, size = 'sm' }) {
  const r = parseNum(rating);
  const starClass = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <Star key={n} className={`${starClass} ${n <= Math.floor(r) ? 'text-amber-400 fill-amber-400' : n === Math.floor(r) + 1 && r % 1 >= 0.5 ? 'text-amber-400 fill-amber-400/50' : 'text-slate-300'}`} />
      ))}
      <span className="ml-1 text-xs text-slate-500" style={{ fontFamily: FONT.data }}>{r.toFixed(1)}</span>
    </div>
  );
}

// ─── KPI CARD ──────────────────────────────────────────────
function KpiCard({ label, value, subtitle, icon: Icon, trend }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 hover:shadow-md transition-shadow duration-200" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-medium uppercase tracking-widest text-slate-400" style={{ fontFamily: FONT.body }}>{label}</span>
        {Icon && <div className="w-8 h-8 rounded-sm bg-slate-50 border border-slate-100 flex items-center justify-center"><Icon className="w-4 h-4 text-slate-500" /></div>}
      </div>
      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{value}</p>
      {subtitle && <p className="text-xs text-slate-400 mt-1" style={{ fontFamily: FONT.body }}>{subtitle}</p>}
    </div>
  );
}

// ─── ACHIEVEMENT BAR (Progress) ────────────────────────────
function AchievementBar({ percent }) {
  const pct = Math.min(parseNum(percent), 150);
  const color = pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className={`text-xs font-semibold min-w-[42px] text-right ${pct >= 100 ? 'text-emerald-600' : pct >= 80 ? 'text-amber-600' : 'text-red-600'}`} style={{ fontFamily: FONT.data }}>{pct.toFixed(1)}%</span>
    </div>
  );
}

// ─── CUSTOM TOOLTIP ────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-lg p-3" style={{ fontFamily: FONT.body }}>
      <p className="text-xs font-semibold text-slate-900 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-slate-600"><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: p.color }} />{p.name}: <span className="font-semibold" style={{ fontFamily: FONT.data }}>{p.name.includes('Sales') ? `₹${formatCurrency(p.value * 100000)}` : `${p.value.toFixed(1)}%`}</span></p>
      ))}
    </div>
  );
}

// ─── DRILL-DOWN PANEL ──────────────────────────────────────
function DrillDownPanel({ submission }) {
  if (!submission) return null;
  const { identity, timing, leads, sales, payments, performance } = submission;
  
  const MetricRow = ({ label, value, highlight }) => (
    <div className="flex justify-between items-center py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-xs text-slate-500" style={{ fontFamily: FONT.body }}>{label}</span>
      <span className={`text-sm font-semibold ${highlight || 'text-slate-900'}`} style={{ fontFamily: FONT.data }}>{value}</span>
    </div>
  );
  
  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center">
            <User className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h3 className="text-slate-900 font-semibold" style={{ fontFamily: FONT.heading }}>{identity?.emp_name}</h3>
            <p className="text-xs text-slate-400" style={{ fontFamily: FONT.data }}>Code: {identity?.emp_code}</p>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Badge variant="outline" className="text-[10px] rounded-sm">{timing?.audit_month}</Badge>
          <Badge variant="outline" className="text-[10px] rounded-sm">Filed: {timing?.date_of_filing}</Badge>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3">
        {[{ label: 'Sales', rating: performance?.sales_rating }, { label: 'Collection', rating: performance?.collection_rating }, { label: 'Overall', rating: performance?.overall_rating }].map(r => (
          <div key={r.label} className="bg-slate-50 border border-slate-100 rounded-sm p-3 text-center">
            <p className="text-[9px] text-slate-400 uppercase mb-1" style={{ fontFamily: FONT.body }}>{r.label}</p>
            <RatingStars rating={r.rating} size="sm" />
          </div>
        ))}
      </div>
      
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2" style={{ fontFamily: FONT.body }}><Target className="w-3.5 h-3.5" /> Sales Performance</h4>
        <div className="bg-slate-50 border border-slate-100 rounded-sm p-3">
          <MetricRow label="Expected Target" value={`₹${formatCurrency(sales?.expected_target)}`} />
          <MetricRow label="Achieved Sales" value={`₹${formatCurrency(sales?.achieved)}`} highlight="text-emerald-600" />
          <MetricRow label="Achievement %" value={`${sales?.achievement_percent?.toFixed(1)}%`} highlight={sales?.achievement_percent >= 100 ? 'text-emerald-600' : sales?.achievement_percent >= 80 ? 'text-amber-600' : 'text-red-600'} />
        </div>
      </div>
      
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2" style={{ fontFamily: FONT.body }}><IndianRupee className="w-3.5 h-3.5" /> Payments</h4>
        <div className="bg-slate-50 border border-slate-100 rounded-sm p-3">
          <MetricRow label="Total Expected" value={`₹${formatCurrency(payments?.total_expected)}`} />
          <MetricRow label="Payment Received" value={`₹${formatCurrency(payments?.received)}`} highlight="text-blue-600" />
          <MetricRow label="Collection %" value={`${payments?.achievement_percent?.toFixed(1)}%`} />
        </div>
      </div>
      
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2" style={{ fontFamily: FONT.body }}><Zap className="w-3.5 h-3.5" /> Leads</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 border border-slate-100 rounded-sm p-3 text-center">
            <p className="text-[10px] text-slate-400 uppercase">Hot Leads</p>
            <p className="text-lg font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{leads?.hot_leads}</p>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-sm p-3 text-center">
            <p className="text-[10px] text-slate-400 uppercase">Self Generated</p>
            <p className="text-lg font-bold text-slate-900" style={{ fontFamily: FONT.data }}>{leads?.self_generated_leads}</p>
          </div>
        </div>
      </div>
      
      {(performance?.feedback || performance?.concerns) && (
        <div className="border-t border-slate-200 pt-4 space-y-3">
          {performance?.feedback && <div><p className="text-[10px] uppercase text-slate-400 mb-1">Feedback</p><p className="text-xs text-slate-700">{performance.feedback}</p></div>}
          {performance?.concerns && performance.concerns !== 'None' && <div><p className="text-[10px] uppercase text-slate-400 mb-1">Concerns</p><p className="text-xs text-amber-600">{performance.concerns}</p></div>}
        </div>
      )}
      
      <div className="border-t border-slate-200 pt-4">
        <h4 className="text-[10px] uppercase text-slate-400 mb-2">Reporting</h4>
        <div className="text-xs space-y-1 text-slate-500">
          <p>Email: <span className="text-slate-700">{identity?.emp_email}</span></p>
          <p>Team Lead: <span className="text-slate-700">{identity?.team_lead_email}</span></p>
          <p>Sales Manager: <span className="text-slate-700">{identity?.sales_manager_email}</span></p>
        </div>
      </div>
    </div>
  );
}

// ─── COUNSELLOR DETAIL VIEW ────────────────────────────────
function CounsellorDetailView({ counsellor, allSubmissions, onBack, onOpenSubmission }) {
  if (!counsellor) return null;
  
  const counsellorSubs = allSubmissions.filter(s => s.mapped_data?.["Emp. Code"] === counsellor.emp_code).sort((a, b) => {
    const monthOrder = { 'jan': 1, 'feb': 2, 'mar': 3, 'apr': 4, 'may': 5, 'jun': 6, 'jul': 7, 'aug': 8, 'sep': 9, 'oct': 10, 'nov': 11, 'dec': 12 };
    const pm = (m) => { if (!m) return { year: 0, month: 0 }; const p = m.toLowerCase().split('-'); return { month: monthOrder[p[0]?.substring(0, 3)] || 0, year: parseInt(p[1]) || 0 }; };
    const aD = pm(a.mapped_data?.["Audit Month"]), bD = pm(b.mapped_data?.["Audit Month"]);
    return (bD.year * 12 + bD.month) - (aD.year * 12 + aD.month);
  });
  
  const trendData = counsellorSubs.map(s => ({
    month: s.mapped_data?.["Audit Month"] || 'Unknown',
    sales: parseNum(s.mapped_data?.["Invoice Sales with Tax Target Achievement %"]),
    collection: parseNum(s.mapped_data?.["Achieved Payment Received with Tax %"]),
  })).reverse();
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-sm transition-colors border border-slate-200"><ArrowLeft className="w-4 h-4 text-slate-600" /></button>
          <div>
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: FONT.heading }}>{counsellor.emp_name}</h2>
            <p className="text-xs text-slate-400" style={{ fontFamily: FONT.data }}>Code: {counsellor.emp_code} | {counsellorSubs.length} submissions</p>
          </div>
        </div>
        <div className={`px-3 py-1.5 rounded-sm text-xs font-semibold border ${getStatusBg(counsellor.avg_achievement_percent)}`} style={{ fontFamily: FONT.data }}>
          Avg: {counsellor.avg_achievement_percent?.toFixed(1)}%
        </div>
      </div>
      
      <div className="grid grid-cols-4 gap-4">
        <KpiCard label="Total Sales" value={`₹${formatCurrency(counsellor.total_achieved_sales)}`} icon={TrendingUp} />
        <KpiCard label="Total Payment" value={`₹${formatCurrency(counsellor.total_payment_received)}`} icon={IndianRupee} />
        <KpiCard label="Total Leads" value={counsellor.total_hot_leads} icon={Zap} />
        <KpiCard label="Avg Rating" value={counsellor.avg_rating?.toFixed(1)} icon={Star} />
      </div>
      
      {trendData.length > 1 && (
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: FONT.heading }}>Performance Trend</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b', fontFamily: FONT.data }} />
              <YAxis tick={{ fontSize: 10, fill: '#64748b', fontFamily: FONT.data }} domain={[0, 'auto']} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: FONT.body }} />
              <Line type="monotone" dataKey="sales" name="Sales %" stroke={COLOR.success} strokeWidth={2} dot={{ r: 4, fill: COLOR.success }} />
              <Line type="monotone" dataKey="collection" name="Collection %" stroke={COLOR.accent} strokeWidth={2} dot={{ r: 4, fill: COLOR.accent }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: FONT.heading }}>All Submissions</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {counsellorSubs.map(s => {
            const md = s.mapped_data || {};
            const salesPct = parseNum(md["Invoice Sales with Tax Target Achievement %"]);
            return (
              <div key={s.id} onClick={() => onOpenSubmission(s.id)} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 cursor-pointer transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-slate-700" style={{ fontFamily: FONT.data }}>{md["Audit Month"]}</span>
                  <span className="text-[10px] text-slate-400">Filed: {md["Date of Filing"]}</span>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right"><p className="text-[9px] uppercase text-slate-400">Sales</p><AchievementBar percent={salesPct} /></div>
                  <RatingStars rating={md["Overall Rating"]} size="sm" />
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN DASHBOARD ────────────────────────────────────────
export default function CareerAdvisorDashboard() {
  const { formId } = useParams();
  const navigate = useNavigate();
  
  const [period, setPeriod] = useState('month');
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedQuarter, setSelectedQuarter] = useState(1);
  const [showTest, setShowTest] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedCounsellor, setSelectedCounsellor] = useState(null);
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [chartView, setChartView] = useState('sales');
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period, year: selectedYear, show_test: showTest };
      if (period === 'month' && selectedMonth) params.month = selectedMonth;
      if (period === 'quarter') params.quarter = selectedQuarter;
      const res = await api.get(`/career-advisor-dashboard/${formId}/team-overview`, { params });
      setData(res.data);
      const subRes = await api.get(`/submissions?form_id=${formId}&limit=500`);
      setAllSubmissions(subRes.data.submissions || []);
      if (!selectedMonth && res.data.available_months?.length > 0) setSelectedMonth(res.data.available_months[0]);
    } catch (err) { toast.error('Failed to load dashboard data'); }
    finally { setLoading(false); }
  }, [formId, period, selectedYear, selectedMonth, selectedQuarter, showTest]);
  
  useEffect(() => { loadData(); }, [loadData]);
  
  const openDrillDown = async (submissionId) => {
    try {
      const res = await api.get(`/career-advisor-dashboard/${formId}/submission/${submissionId}`);
      setSelectedSubmission(res.data);
      setDrillDownOpen(true);
    } catch { toast.error('Failed to load submission details'); }
  };
  
  // Grouped bar chart data: Expected vs Achieved
  const chartData = data?.counsellors?.map(c => {
    const paymentExpected = c.total_expected_payment || 0;
    const paymentAchieved = c.total_payment_received || 0;
    const paymentPct = paymentExpected > 0 ? (paymentAchieved / paymentExpected) * 100 : 0;
    return {
      name: c.emp_name.split(' ')[0],
      expected: chartView === 'sales' ? c.total_expected_sales / 100000 : paymentExpected / 100000,
      achieved: chartView === 'sales' ? c.total_achieved_sales / 100000 : paymentAchieved / 100000,
      achievement: chartView === 'sales' ? c.avg_achievement_percent : paymentPct,
    };
  }) || [];
  const chartTitle = chartView === 'sales' ? 'Expected vs Achieved Sales' : 'Expected vs Achieved Payments';
  const chartSubtitle = chartView === 'sales' ? 'Amounts in Lakhs (₹L) | Target comparison by employee' : 'Amounts in Lakhs (₹L) | Payment collection comparison by employee';
  
  const NAV = [
    { id: 'overview', label: 'Team Overview', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];
  
  return (
    <div className="flex h-screen bg-white overflow-hidden" style={{ fontFamily: FONT.body }} data-testid="career-advisor-dashboard">
      {/* Sidebar */}
      <aside className="w-56 bg-slate-900 flex flex-col flex-shrink-0">
        <div className="p-5 border-b border-slate-800">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-7 h-7 rounded-sm bg-white/10 flex items-center justify-center"><Users className="w-4 h-4 text-white" /></div>
            <p className="text-white text-sm font-bold" style={{ fontFamily: FONT.heading }}>Career Advisor</p>
          </div>
          <p className="text-slate-500 text-[10px] pl-9 leading-tight">Team Performance Audit</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 mt-2">
          {NAV.map(item => (
            <button key={item.id} data-testid={`nav-${item.id}`} onClick={() => { setActiveTab(item.id); setSelectedCounsellor(null); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-xs font-medium transition-all duration-150 ${activeTab === item.id && !selectedCounsellor ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}>
              <item.icon className="w-3.5 h-3.5 flex-shrink-0" />{item.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <button onClick={() => navigate('/dashboard')} data-testid="back-to-main" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-sm text-xs text-slate-500 hover:bg-slate-800 hover:text-white transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" />Back to Command Center
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-6 gap-4 flex-shrink-0">
          <h1 className="text-sm font-bold text-slate-900 flex-1 truncate" style={{ fontFamily: FONT.heading }}>{data?.form?.name || 'Career Advisor Dashboard'}</h1>
          
          <div className="flex items-center gap-2">
            <div className="flex border border-slate-200 rounded-sm overflow-hidden">
              {['month', 'quarter', 'year'].map(p => (
                <button key={p} onClick={() => setPeriod(p)} data-testid={`period-${p}`}
                  className={`px-3 py-1.5 text-[10px] font-semibold uppercase transition-colors ${period === p ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>{p}
                </button>
              ))}
            </div>
            
            {period === 'month' && (
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-32 h-8 rounded-sm bg-white border-slate-200 text-slate-700 text-xs"><SelectValue placeholder="Select month" /></SelectTrigger>
                <SelectContent>{(data?.available_months || []).map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {period === 'quarter' && (
              <Select value={String(selectedQuarter)} onValueChange={v => setSelectedQuarter(Number(v))}>
                <SelectTrigger className="w-20 h-8 rounded-sm bg-white border-slate-200 text-slate-700 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4].map(q => <SelectItem key={q} value={String(q)} className="text-xs">Q{q}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-20 h-8 rounded-sm bg-white border-slate-200 text-slate-700 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{[2026, 2025, 2024].map(y => <SelectItem key={y} value={String(y)} className="text-xs">{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          
          <button onClick={() => setShowTest(v => !v)} data-testid="show-test-toggle"
            className={`h-8 px-3 rounded-sm text-[10px] font-semibold uppercase border transition-colors ${showTest ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-white border-slate-200 text-slate-400 hover:text-slate-600'}`}>
            {showTest ? 'Incl. Test' : 'Excl. Test'}
          </button>
          <button onClick={loadData} data-testid="refresh-btn" className="h-8 w-8 flex items-center justify-center rounded-sm bg-white border border-slate-200 text-slate-400 hover:text-slate-600 transition-colors">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </header>
        
        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400 mx-auto" /><p className="text-slate-400 text-sm mt-3">Loading dashboard...</p></div>
            </div>
          ) : selectedCounsellor ? (
            <CounsellorDetailView counsellor={selectedCounsellor} allSubmissions={allSubmissions} onBack={() => setSelectedCounsellor(null)} onOpenSubmission={openDrillDown} />
          ) : activeTab === 'overview' ? (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="kpi-cards">
                <KpiCard label="Employees" value={data?.team_totals?.total_counsellors || 0} icon={Users} />
                <KpiCard label="Submissions" value={data?.team_totals?.total_submissions || 0} icon={FileText} />
                <KpiCard label="Achieved Sales" value={`₹${formatCurrency(data?.team_totals?.total_achieved_sales)}`} icon={TrendingUp} />
                <KpiCard label="Achieved Payments" value={`₹${formatCurrency(data?.team_totals?.total_payment_received)}`} icon={IndianRupee} />
                <KpiCard label="Top Performer" value={data?.team_totals?.top_performer || '-'} icon={Award} />
              </div>
              
              {/* Grouped Bar Chart: Expected vs Achieved */}
              {chartData.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-sm p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: FONT.heading }}>{chartTitle}</h3>
                      <p className="text-[10px] text-slate-400 mt-0.5">{chartSubtitle}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* View Toggle */}
                      <div className="flex border border-slate-200 rounded-sm overflow-hidden" data-testid="chart-view-toggle">
                        <button onClick={() => setChartView('sales')} data-testid="chart-view-sales"
                          className={`px-3 py-1.5 text-[10px] font-semibold transition-colors ${chartView === 'sales' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                          Sales
                        </button>
                        <button onClick={() => setChartView('payments')} data-testid="chart-view-payments"
                          className={`px-3 py-1.5 text-[10px] font-semibold transition-colors ${chartView === 'payments' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                          Payments
                        </button>
                      </div>
                      {/* Legend */}
                      <div className="flex items-center gap-4 text-[10px]">
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-200" />Expected</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-900" />Achieved</span>
                      </div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 5 }} barGap={4} barSize={32}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#334155', fontFamily: FONT.body, fontWeight: 600 }} axisLine={{ stroke: '#e2e8f0' }} />
                      <YAxis tick={{ fontSize: 10, fill: '#64748b', fontFamily: FONT.data }} axisLine={{ stroke: '#e2e8f0' }} tickFormatter={v => `₹${v}L`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="expected" name={chartView === 'sales' ? 'Expected Sales' : 'Expected Payment'} fill="#e2e8f0" radius={[2, 2, 0, 0]}>
                        <LabelList dataKey="expected" position="top" formatter={v => `₹${v.toFixed(1)}L`} style={{ fontSize: 9, fill: '#94a3b8', fontFamily: FONT.data }} />
                      </Bar>
                      <Bar dataKey="achieved" name={chartView === 'sales' ? 'Achieved Sales' : 'Payment Received'} radius={[2, 2, 0, 0]}>
                        {chartData.map((entry, i) => (
                          <Cell key={i} fill={getStatusColor(entry.achievement)} />
                        ))}
                        <LabelList dataKey="achieved" position="top" formatter={v => `₹${v.toFixed(1)}L`} style={{ fontSize: 9, fill: '#0f172a', fontWeight: 600, fontFamily: FONT.data }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {/* Color Code Legend */}
                  <div className="flex items-center justify-center gap-6 mt-4 pt-3 border-t border-slate-100" data-testid="color-legend">
                    <span className="flex items-center gap-1.5 text-[10px] font-medium text-slate-600"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />Target Exceeded (≥100%)</span>
                    <span className="flex items-center gap-1.5 text-[10px] font-medium text-slate-600"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" />On Track (60–99%)</span>
                    <span className="flex items-center gap-1.5 text-[10px] font-medium text-slate-600"><span className="w-2.5 h-2.5 rounded-full bg-red-500" />Below Target (&lt;60%)</span>
                  </div>
                </div>
              )}
              
              {/* Team Performance Table */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: FONT.heading }}>Team Performance</h3>
                    <p className="text-[10px] text-slate-400 mt-0.5">Click on an employee to view detailed monthly, quarterly & yearly breakdown</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-[9px] font-medium text-slate-500"><span className="w-2 h-2 rounded-full bg-emerald-500" />≥100%</span>
                    <span className="flex items-center gap-1.5 text-[9px] font-medium text-slate-500"><span className="w-2 h-2 rounded-full bg-amber-500" />60–99%</span>
                    <span className="flex items-center gap-1.5 text-[9px] font-medium text-slate-500"><span className="w-2 h-2 rounded-full bg-red-500" />&lt;60%</span>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 border-slate-200 hover:bg-slate-50">
                      <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold" style={{ fontFamily: FONT.body }}>Employee</TableHead>
                      <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Hot Leads</TableHead>
                      <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Expected</TableHead>
                      <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Achieved</TableHead>
                      <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">Achievement</TableHead>
                      <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Total Pmt Expected</TableHead>
                      <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Payment Recv</TableHead>
                      <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Collection %</TableHead>
                      <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-center">Rating</TableHead>
                      <TableHead className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold text-right">Entries</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.counsellors?.map((c, idx) => (
                      <TableRow key={c.emp_code} className="border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setSelectedCounsellor(c)} data-testid={`counsellor-row-${c.emp_code}`}>
                        <TableCell className="py-3">
                          <div className="flex items-center gap-2">
                            {idx === 0 && <Award className="w-3.5 h-3.5 text-amber-500" />}
                            <div>
                              <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: FONT.body }}>{c.emp_name}</p>
                              <p className="text-[10px] text-slate-400" style={{ fontFamily: FONT.data }}>{c.emp_code}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right text-xs font-semibold text-slate-700" style={{ fontFamily: FONT.data }}>{c.total_hot_leads || 0}</TableCell>
                        <TableCell className="text-right text-xs text-slate-500" style={{ fontFamily: FONT.data }}>₹{formatCurrency(c.total_expected_sales)}</TableCell>
                        <TableCell className="text-right text-xs font-semibold text-slate-900" style={{ fontFamily: FONT.data }}>₹{formatCurrency(c.total_achieved_sales)}</TableCell>
                        <TableCell><AchievementBar percent={c.avg_achievement_percent} /></TableCell>
                        <TableCell className="text-right text-xs text-slate-500" style={{ fontFamily: FONT.data }}>₹{formatCurrency(c.total_expected_payment)}</TableCell>
                        <TableCell className="text-right text-xs text-slate-600" style={{ fontFamily: FONT.data }}>₹{formatCurrency(c.total_payment_received)}</TableCell>
                        <TableCell className="text-right text-xs font-semibold" style={{ fontFamily: FONT.data }}>
                          {(() => {
                            const exp = Number(c.total_expected_payment || 0);
                            const rec = Number(c.total_payment_received || 0);
                            if (exp <= 0) return <span className="text-slate-300">—</span>;
                            const pct = (rec / exp) * 100;
                            const cls = pct >= 100 ? 'text-emerald-600' : pct >= 60 ? 'text-amber-600' : 'text-red-600';
                            return <span className={cls}>{pct.toFixed(1)}%</span>;
                          })()}
                        </TableCell>
                        <TableCell className="text-center"><RatingStars rating={c.avg_rating} /></TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1"><span className="text-xs text-slate-500" style={{ fontFamily: FONT.data }}>{c.submission_count}</span><ChevronRight className="w-3.5 h-3.5 text-slate-300" /></div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!data?.counsellors || data.counsellors.length === 0) && (
                      <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-12"><AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" /><p>No employee data for this period</p></TableCell></TableRow>
                    )}
                    {data?.counsellors?.length > 0 && (
                      <TableRow className="bg-slate-100/80 border-t-2 border-slate-300 font-semibold">
                        <TableCell className="py-3 text-xs text-slate-700" style={{ fontFamily: FONT.body }}>Total ({data.counsellors.length})</TableCell>
                        <TableCell className="text-right text-xs text-slate-900" style={{ fontFamily: FONT.data }}>{data.counsellors.reduce((s, c) => s + (c.total_hot_leads || 0), 0)}</TableCell>
                        <TableCell className="text-right text-xs text-slate-700" style={{ fontFamily: FONT.data }}>₹{formatCurrency(data.counsellors.reduce((s, c) => s + (c.total_expected_sales || 0), 0))}</TableCell>
                        <TableCell className="text-right text-xs text-slate-900" style={{ fontFamily: FONT.data }}>₹{formatCurrency(data.counsellors.reduce((s, c) => s + (c.total_achieved_sales || 0), 0))}</TableCell>
                        <TableCell className="text-xs text-slate-500" />
                        <TableCell className="text-right text-xs text-slate-700" style={{ fontFamily: FONT.data }}>₹{formatCurrency(data.counsellors.reduce((s, c) => s + (c.total_expected_payment || 0), 0))}</TableCell>
                        <TableCell className="text-right text-xs text-slate-700" style={{ fontFamily: FONT.data }}>₹{formatCurrency(data.counsellors.reduce((s, c) => s + (c.total_payment_received || 0), 0))}</TableCell>
                        <TableCell className="text-right text-xs font-semibold" style={{ fontFamily: FONT.data }}>
                          {(() => {
                            const exp = data.counsellors.reduce((s, c) => s + (Number(c.total_expected_payment) || 0), 0);
                            const rec = data.counsellors.reduce((s, c) => s + (Number(c.total_payment_received) || 0), 0);
                            if (exp <= 0) return <span className="text-slate-300">—</span>;
                            const pct = (rec / exp) * 100;
                            const cls = pct >= 100 ? 'text-emerald-600' : pct >= 60 ? 'text-amber-600' : 'text-red-600';
                            return <span className={cls}>{pct.toFixed(1)}%</span>;
                          })()}
                        </TableCell>
                        <TableCell />
                        <TableCell className="text-right text-xs text-slate-700" style={{ fontFamily: FONT.data }}>{data.counsellors.reduce((s, c) => s + (c.submission_count || 0), 0)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="space-y-4 max-w-2xl">
              <div className="bg-white border border-slate-200 rounded-sm p-5">
                <h3 className="text-sm font-bold text-slate-900 mb-4" style={{ fontFamily: FONT.heading }}>Column Selection</h3>
                <p className="text-xs text-slate-400 mb-3">Configure which columns appear in the team table. (Coming soon)</p>
                <div className="grid grid-cols-2 gap-2">
                  {['Emp Code', 'Expected Sales', 'Achieved Sales', 'Achievement %', 'Payment Received', 'Rating', 'Hot Leads'].map(col => (
                    <label key={col} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer hover:text-slate-900">
                      <input type="checkbox" defaultChecked className="rounded border-slate-300" />{col}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex justify-end"><Button className="rounded-sm" disabled>Save Settings (Coming Soon)</Button></div>
            </div>
          )}
        </main>
      </div>
      
      {/* Drill-down Side Panel */}
      <Sheet open={drillDownOpen} onOpenChange={setDrillDownOpen}>
        <SheetContent className="w-[420px] bg-white overflow-y-auto">
          <SheetHeader className="mb-4"><SheetTitle className="text-slate-900 text-base font-bold" style={{ fontFamily: FONT.heading }}>Submission Details</SheetTitle></SheetHeader>
          <DrillDownPanel submission={selectedSubmission} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
